import { Button } from '../ui/Button';
import { Dialog } from 'primereact/dialog';
import { Toolbar } from 'primereact/toolbar';
import { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useDispatch } from 'react-redux';
import { AllRoleItemDto } from '../../dtos/role/AllRoleItemDto';
import { InputText } from 'primereact/inputtext';
import { createEdition, deleteEditionById, getAllEdition, getEditionById, updateEditionById } from '../../service/edition/EditionService';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';
import { EditionFeatureDto } from '../../dtos/edition/EditionFeatureDto';
import { FEATURES_KEYS } from '../../shared/constant/Features';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Card } from 'primereact/card';
import { showToast } from '../../store/slices/toastSlice';

export interface IEdition {
    name: string;
    displayName: string;
    description: string;
    features: string[];
    yearlyAmount: number;
    weeklyAmount: number;
    monthlyAmount: number;
    [key: string]: any;
}
export interface IEditionError {
    name: boolean;
    displayName: boolean;
    description: boolean;
    features: boolean;
    yearlyAmount: boolean;
    weeklyAmount: boolean;
    monthlyAmount: boolean;
    [key: string]: any;
}


export const Edition = () => {
    const emptyEdition: IEdition = {
        name: '',
        displayName: "",
        description: '',
        features: [],
        yearlyAmount: 0,
        weeklyAmount: 0,
        monthlyAmount: 0
    }
    const initialError: IEditionError = {
        name: false,
        displayName: false,
        description: false,
        features: false,
        yearlyAmount: false,
        weeklyAmount: false,
        monthlyAmount: false
    }
    const dispatch = useDispatch();
    const [showCreateEditionDialog, setShowCreateEditionDialog] = useState<boolean>(false);
    const [allEditions, setAllEditions] = useState<AllRoleItemDto[]>([]);
    const [edit, setEdit] = useState<boolean>(false);
    const [edition, setEdition] = useState(emptyEdition);
    const toast = useRef<Toast>(null);
    const [editionId, setEditionId] = useState<number | null>(null)
    const [features, setFeatures] = useState<EditionFeatureDto[] | []>([])
    const [errorIndicator, setErrorIndicator] = useState<IEditionError>(initialError)
    const [loading, setLoading] = useState(true);

    const getAllEditionData = async () => {
        try {
            setLoading(true);
            const roles = await getAllEdition(dispatch);
            if (roles) {
                setAllEditions(roles);
            }
        } catch (error) {
            console.error('Error fetching editions:', error);
            dispatch(showToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load editions',
                life: 3000
            }));
        } finally {
            setLoading(false);
        }
    }

    const show = (severity: any, summary: string, detail: string) => {
        toast && toast?.current?.show({ severity, summary, detail });
    };

    type Feature = { name: string; code: string };

    const onInputChange = (
        value: string | number | Feature[] | null | undefined,
        name: keyof IEdition
    ) => {
        const roleData: IEdition = { ...edition };

        // 🔹 CASE 1: Features
        if (Array.isArray(value)) {
            const featureCodes = value.map((f) => f.code);
            roleData.features = featureCodes;

            setErrorIndicator((prev) => ({
                ...prev,
                [name]: featureCodes.length === 0,
            }));
        }

        // 🔹 CASE 2: String
        else if (typeof value === "string") {
            roleData[name] = value;

            setErrorIndicator((prev) => ({
                ...prev,
                [name]: value.trim().length === 0,
            }));
        }

        // 🔹 CASE 3: Number / null / undefined (InputNumber)
        else {
            const numericValue =
                typeof value === "number" && !Number.isNaN(value) ? value : 0;

            roleData[name] = numericValue as IEdition[keyof IEdition];

            setErrorIndicator((prev) => ({
                ...prev,
                [name]: numericValue === 0,
            }));
        }

        setEdition(roleData);
    };

    const formFeatures = (featuresArray: string[]) => {
        return featuresArray.filter((key) => FEATURES_KEYS[key]).map((key) => ({
            name: FEATURES_KEYS[key].displayName,
            code: key
        }));
    }
    const onEdit = async (rowData: any) => {
        setShowCreateEditionDialog(true);
        setEdit(true);
        setEditionId(rowData.id)
        const editionToEdit = await getEditionById(dispatch, rowData.id)
        const extractedFeatures = extractFeatures()
        setFeatures(extractedFeatures)

        // const selectedFeatures = editionToEdit.features.map((key: string) => ({
        //     name: FEATURES_KEYS[key].displayName,
        //     code: key
        // }));
        const selectedFeatures = formFeatures(editionToEdit.features)
        const editionData: IEdition = {
            id: rowData.id,
            name: rowData.name,
            displayName: rowData.displayName,
            description: rowData.description,
            features: selectedFeatures.map((feature: any) => feature.code),
            yearlyAmount: rowData.yearlyAmount,
            weeklyAmount: rowData.weeklyAmount,
            monthlyAmount: rowData.monthlyAmount
        }


        setEdition({ ...editionData });
    }

    const extractFeatures = () => {
        return Object.keys(FEATURES_KEYS).map((key) => ({
            name: FEATURES_KEYS[key].displayName, // what user sees
            code: key,                              // actual permission key
        }));
    };


    const openNew = () => {
        const extractedFeatures = extractFeatures()
        setFeatures(extractedFeatures)
        setErrorIndicator(initialError)
        setShowCreateEditionDialog(true);
        setEdition(emptyEdition);
    }

    const accept = async (id: number) => {
        await deleteEditionById(dispatch, id);
        show('success', 'Success', 'Edition deleted successfully')
        await getAllEditionData();
    }

    const reject = () => {
        show('warn', 'Rejected', 'You have rejected')
    }

    const confirm = (id: number) => {
        confirmDialog({
            message: (
                <div className="text-center p-4">
                    <p className="mb-5 text-base font-medium">
                        Do you really want to delete this <strong>edition</strong>?
                    </p>
                </div>
            ),
            header: `${"Delete Confirmation"}`,
            className: "del_modal",
            acceptClassName: "p-button-danger",
            accept: () => accept(id),
            reject,
        });
    };

    const handleError = () => {
        // Perform validation and set error state
        const newErrorIndicator = {
            ...initialError,
            name: !edition.name,
            displayName: !edition.displayName,
            features: edition.features.length === 0,
        };
        setErrorIndicator(newErrorIndicator);
        return newErrorIndicator
    };

    const handleSave = async () => {
        const errors = handleError();

        if (Object.values(errors).some(error => error)) {
            show("error", "Validation Error", "Please fill in all required fields.");
            return;
        }

        if (edit && editionId) {
            await updateEditionById(dispatch, editionId, edition);
            show('success', 'Success', 'Edition updated successfully')
        }
        else {
            const createdEdition = await createEdition(dispatch, { ...edition });
            if (createdEdition) {
                show("success", "Success", "Edition created successfully")
            }

        }

        hideEditionDialog()
        await getAllEditionData();
    }

    const hideEditionDialog = () => {
        setShowCreateEditionDialog(false);
        setFeatures([])
        setEdit(false)
        setErrorIndicator(initialError)
        setEdition(emptyEdition)

    }

    const startToolbar = () => {
        return (
            <div>
                <h4 className="text-xl font-bold surface-900 mb-4 mt-3">Editions</h4>
            </div>
        )
    }

    const endToolbar = () => {
        return (
            <div>
                <Button children='Create' onClick={openNew} />
            </div>
        )
    }

    const actionBodyTemplate = (rowData: AllRoleItemDto) => {
        return (
            <div className="flex">
                <Button children={<FontAwesomeIcon icon={faPen} />} className="actionBtn bg-transparent text-gold hover:text-white hover:bg-gold hover:border-gold mr-2" onClick={() => onEdit(rowData)} />
                <Button className='actionBtn border-red-500 bg-transparent hover:bg-red-500 text-red-500 hover:text-white' children={<FontAwesomeIcon icon={faTrash} />} onClick={() => confirm(rowData.id as number)} />
            </div>
        );
    };

    const createRoleDialogFooter = () => {
        return (
            <div className="flex justify-end gap-2 mt-4">
                <Button children='Save' onClick={handleSave} />
                <Button children='Cancel' onClick={hideEditionDialog} className="p-button-outlined" />
            </div>
        )
    }

    useEffect(() => {
        getAllEditionData()
    }, [])



    return (
        <div className="p-0 edition">
            <Toast ref={toast} />
            <ConfirmDialog />
            <Toolbar start={startToolbar} end={endToolbar} className="mb-4 bg-transparent border-none p-0" />

            <Card className="rounded-md shadow-sm card_content_padding ">
                <DataTable value={allEditions} className="p-datatable-sm">
                    <Column field='name' header='Name'></Column>
                    <Column field='displayName' header='Display Name'></Column>
                    <Column field='description' header='Description'></Column>
                    <Column field='yearlyAmount' header='Yearly Amount'></Column>
                    <Column field='monthlyAmount' header='Monthly Amount'></Column>
                    <Column field='weeklyAmount' header='Weekly Amount'></Column>
                    <Column header="Actions" body={actionBodyTemplate}></Column>
                </DataTable>
            </Card>

            <Dialog
                visible={showCreateEditionDialog}
                header="Create New Edition"
                onHide={hideEditionDialog}
                style={{ width: '40rem' }}
                footer={createRoleDialogFooter}
                breakpoints={{ '960px': '75vw', '640px': '90vw' }}
                className="p-fluid"
            >
                <div className="grid grid-cols-1 gap-4 pt-4">
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1" htmlFor="name">Name</label>
                        <InputText
                            id="name"
                            type="text"
                            value={edition.name}
                            onChange={(e) => onInputChange(e?.target?.value, 'name')}
                            className={errorIndicator.name ? "p-invalid w-full" : "w-full"}
                        />
                        {errorIndicator.name && <small className="text-red-500">Name is required</small>}
                    </div>

                    <div className="mb-0">
                        <label className="block text-sm font-medium mb-1" htmlFor="displayName">Display Name</label>
                        <InputText
                            id="displayName"
                            value={edition?.displayName}
                            onChange={(e) => onInputChange(e?.target?.value, 'displayName')}
                            className={errorIndicator.displayName ? "p-invalid w-full" : "w-full"}
                        />
                        {errorIndicator.displayName && <small className="text-red-500">Display Name is required</small>}
                    </div>

                    <div className="mb-0">
                        <label className="block text-sm font-medium mb-1" htmlFor="description">Description</label>
                        <InputText
                            id="description"
                            value={edition?.description}
                            onChange={(e) => onInputChange(e?.target?.value, 'description')}
                            className="w-full"
                        />
                    </div>

                    <div className="mb-0">
                        <label className="block text-sm font-medium mb-1" htmlFor="features">Select Features</label>
                        <MultiSelect
                            id="features"
                            value={formFeatures(edition.features)}
                            onChange={(e) => {
                                onInputChange(e?.value, 'features')
                                // setSelectedFeatures(e.value)
                            }}
                            options={features}
                            optionLabel="name"
                            display="chip"
                            placeholder="Select Features"
                            maxSelectedLabels={3}
                            className={errorIndicator.features ? "p-invalid w-full" : "w-full"}
                        />
                        {errorIndicator.features && <small className="text-red-500">At least one feature must be selected</small>}
                    </div>

                    <div className="mb-0">
                        <label className="block text-sm font-medium mb-1" htmlFor="yearlyAmount">Yearly Amount</label>
                        <InputNumber
                            id="yearlyAmount"
                            //value={yearlyAmount}
                            value={edition.yearlyAmount}
                            // onChange={(e) => setYearlyAmount(e?.value as number)}
                            onChange={(e) => onInputChange(e?.value as number, 'yearlyAmount')}
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            className={errorIndicator.yearlyAmount ? "p-invalid w-full" : "w-full"}
                        />
                    </div>

                    <div className="mb-0">
                        <label className="block text-sm font-medium mb-1" htmlFor="monthlyAmount">Monthly Amount</label>
                        <InputNumber
                            id="monthlyAmount"
                            //value={monthlyAmount}
                            value={edition.monthlyAmount}
                            onChange={(e) => onInputChange(e?.value as number, 'monthlyAmount')}
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            className={errorIndicator.monthlyAmount ? "p-invalid w-full" : "w-full"}
                        />
                    </div>

                    <div className="mb-0">
                        <label className="block text-sm font-medium mb-1" htmlFor="weeklyAmount">Weekly Amount</label>
                        <InputNumber
                            id="weeklyAmount"
                            value={edition.weeklyAmount}
                            onChange={(e) => onInputChange(e?.value as number, 'weeklyAmount')}
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            className={errorIndicator.weeklyAmount ? "p-invalid w-full" : "w-full"}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    )
}